import React, { useState, forwardRef, Fragment } from 'react';
import AccordionToggleIcon from 'Icons/caretGsd.svg?react';
import { IImageBaseViewModel } from 'Types/Litium';
import { dpr } from 'Utils/dpr';

interface AccordionChildProps {
    isOpen?: boolean;
    onToggle?: (index: number) => void;
    title: string;
    icon?: IImageBaseViewModel;
    index?: number;
    children: React.ReactElement;
}

export const AccordionChild = ({
    isOpen,
    onToggle,
    title,
    children,
    icon,
    index,
}: AccordionChildProps) => (
    <>
        <li className="my-4">
            <button
                type="button"
                onClick={() => onToggle(index)}
                className="flex w-full items-center justify-between font-montserrat text-base leading-normal text-black"
            >
                {icon && (
                    <figure className="flex h-10 w-10 items-center">
                        <img
                            src={icon.src}
                            srcSet={dpr(icon.src)}
                            alt={icon.altText}
                            width={icon.width > 0 ? icon.width : ''}
                            height={icon.height > 0 ? icon.height : ''}
                        />
                    </figure>
                )}
                {title}
                <AccordionToggleIcon
                    className={`h-4 w-4 transition-transform duration-layout ${
                        isOpen ? 'rotate-90' : 'rotate-0'
                    }`}
                />
            </button>
            <div
                className={`grid transition-[grid-template-rows,_visibility] duration-layout ${
                    isOpen
                        ? 'visible grid-rows-[1fr]'
                        : 'invisible grid-rows-[0fr]'
                }`}
            >
                <ul className="overflow-hidden">
                    <li>{children}</li>
                </ul>
            </div>
        </li>
        <li>
            <hr className="divider-horizontal" />
        </li>
    </>
);

export const Accordion = forwardRef<
    HTMLUListElement,
    {
        children: React.ReactNode | string;
        className?: string;
    }
>(({ children, className = '' }, ref) => {
    const [openIndex, setOpenIndex] = useState(-1);
    const toggleAccordion = (index: number) => {
        setOpenIndex(prevOpenIndex => (prevOpenIndex === index ? -1 : index));
    };
    return (
        <ul
            ref={ref}
            className={`-:before:divider-horizontal -:grid -:w-full -:grid-cols-1 -:justify-center -:before:w-full -:before:border-b ${className}`}
        >
            {React.Children.map(children, (child, index) => (
                <Fragment key={index}>
                    {child
                        ? React.cloneElement(child as React.ReactElement, {
                              isOpen: index === openIndex,
                              onToggle: () => toggleAccordion(index),
                              index,
                          })
                        : null}
                </Fragment>
            ))}
        </ul>
    );
});

export default Accordion;
